import { useState, useEffect, useCallback, useRef } from 'react';
import scraperApi from 'api';
import { formatStatusDisplay, isStatusRunning } from 'pages/crawler/utils/crawlerStatusUtils';
import type { CrawlerJob, CrawlerSummary } from 'pages/crawler/components/jobs/types';
import { JOBS_PAGE_SIZE, JOBS_POLL_INTERVAL } from 'pages/crawler/constants/crawlerConstants';

interface UseCrawlerJobsPollingOptions {
  crawlerId?: string;
  interval?: number;    // defaults to JOBS_POLL_INTERVAL
  enabled?: boolean;
}

interface UseCrawlerJobsPollingReturn {
  jobs: CrawlerJob[];
  currentCrawler: CrawlerSummary | null;
  isLoading: boolean;
  error: string | null;
  hasNextPage: boolean;
  loadMoreJobs: () => Promise<void>;
  refetch: () => Promise<void>;
}

const toJobs = (data: any[]): CrawlerJob[] =>
  data.map((job: any) => ({
    ...job,
    status: formatStatusDisplay(job.status),
  }));

const toCrawlerSummary = (data: any): CrawlerSummary => ({
  id: data.id,
  name: data.name,
  createdAt: data.createdAt,
  status: formatStatusDisplay(data.status),
  apiParams: data.apiParams,
  traits: data.traits || 0,
});

export const useCrawlerJobsPolling = ({
  crawlerId,
  interval = JOBS_POLL_INTERVAL,
  enabled = true,
}: UseCrawlerJobsPollingOptions): UseCrawlerJobsPollingReturn => {
  const [jobs, setJobs] = useState<CrawlerJob[]>([]);
  const [currentCrawler, setCurrentCrawler] = useState<CrawlerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);

  const maxPageLoadedRef = useRef(1);
  const isInitialLoadRef = useRef(true);

  // Fetch crawler + jobs data
  const fetchData = useCallback(async (abortSignal?: AbortSignal) => {
    if (!crawlerId || !enabled) return;

    try {
      setError(null);

      if (isInitialLoadRef.current) {
        setIsLoading(true);
      }

      const limit = maxPageLoadedRef.current * JOBS_PAGE_SIZE;

      const [crawlerResponse, jobsResponse] = await Promise.all([
        scraperApi.crawlers.getCrawlerById(crawlerId, { signal: abortSignal }),
        scraperApi.crawlers.getCrawlerJobs(crawlerId, { page: 1, limit }, { signal: abortSignal }),
      ]);

      if (abortSignal?.aborted) return;

      if (crawlerResponse.success && crawlerResponse.data) {
        setCurrentCrawler(toCrawlerSummary(crawlerResponse.data));
      }

      if (jobsResponse.success && jobsResponse.data) {
        setJobs(toJobs(jobsResponse.data.jobs || []));
        setHasNextPage(jobsResponse.data.pagination?.hasNextPage ?? false);
      }

      isInitialLoadRef.current = false;
    } catch (err: any) {
      if (err.name === 'AbortError' || abortSignal?.aborted) return;

      console.error('Error fetching crawler jobs:', err);
      setError(err.message || 'Failed to fetch data');

      if (isInitialLoadRef.current) {
        setJobs([]);
        setCurrentCrawler(null);
      }
    } finally {
      if (!abortSignal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [crawlerId, enabled]);

  // Load next page of jobs
  const loadMoreJobs = useCallback(async () => {
    if (!crawlerId || !hasNextPage) return;

    const nextPage = maxPageLoadedRef.current + 1;

    try {
      const response = await scraperApi.crawlers.getCrawlerJobs(
        crawlerId,
        { page: nextPage, limit: JOBS_PAGE_SIZE },
      );

      if (response.success && response.data) {
        maxPageLoadedRef.current = nextPage;
        setJobs((prev) => [...prev, ...toJobs(response.data.jobs || [])]);
        setHasNextPage(response.data.pagination?.hasNextPage ?? false);
      }
    } catch (err: any) {
      console.error('Error loading more jobs:', err);
      setError(err.message || 'Failed to load more jobs');
    }
  }, [crawlerId, hasNextPage]);

  // Manual refetch
  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Conditional polling: only when there are active jobs
  const hasActiveJobs = jobs.some(
    (job) => isStatusRunning(job.status) || job.status?.state === 'scheduled'
  );

  // Initial fetch
  useEffect(() => {
    if (!enabled || !crawlerId) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [enabled, crawlerId, fetchData]);

  // Conditional polling
  useEffect(() => {
    if (!hasActiveJobs || !enabled || !crawlerId) return;

    const pollTimer = setInterval(() => {
      fetchData();
    }, interval);

    return () => clearInterval(pollTimer);
  }, [hasActiveJobs, enabled, crawlerId, interval, fetchData]);

  // Refetch on tab visibility change
  useEffect(() => {
    if (!enabled || !crawlerId) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, crawlerId, fetchData]);

  return {
    jobs,
    currentCrawler,
    isLoading,
    error,
    hasNextPage,
    loadMoreJobs,
    refetch,
  };
};
